/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2025 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { useEffect, useMemo, useState } from "react";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ICustomerPACS } from "@src/types/customerPACS";
import { useAppDispatch } from "@src/config/store";
import axios from "axios";
import { InstructionAction } from "@src/reducers/slices/instructionSlice";
import { CustomerPACSStatus, PaginationResponse } from "@advapacs/common";
import { customerPACSApi } from "@src/config/api";
import { axiosInstanceRegion } from "@src/config/axios-region";

const InstructionStartedIcon: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [showPopup, setShowPopup] = useState(true);
  const [disableComplete, setDisableComplete] = useState(false);
  const [data, setData] = useState<Array<ICustomerPACS>>([]);
  const [count, setCount] = useState(1);
  const [, setError] = useState({});

  const fetchData = async () => {
    try {
      const composedUrl = `${customerPACSApi.GET_CUSTOMER_PACS}`;
      const params = { mode: "dropdown" };
      const response = await axiosInstanceRegion.get(composedUrl, {
        params,
      });
      const rs = response?.data as PaginationResponse<ICustomerPACS>;
      setData(rs?.content);
    } catch (error) {
      setError(error);
    }
  };

  const togglePopup = async () => {
    setShowPopup(!showPopup);
    if (!showPopup) {
      await fetchData();
    }
  };

  // DidMount
  useEffect(() => {
    fetchData();
  }, []);

  const isShowRequestAccessMessage = useMemo(() => {
    const isShow = data.some(
      (e) =>
        e.status === CustomerPACSStatus.PENDING ||
        e.status === CustomerPACSStatus.ACCEPTED
    );
    dispatch(InstructionAction.setHasRequestAccess(isShow));
    return isShow;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const isShowViewStudy = useMemo(() => {
    const isShow = data.some((e) => e.status === CustomerPACSStatus.ACCEPTED);
    dispatch(InstructionAction.setAccepted(isShow));
    return isShow;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (!data || data.length <= 0) return;
    let countStep = 1;
    if (
      data?.some(
        (e) =>
          e.status === CustomerPACSStatus.PENDING ||
          e.status === CustomerPACSStatus.ACCEPTED
      )
    )
      countStep += 1;
    if (data.some((e) => e.status === CustomerPACSStatus.ACCEPTED))
      countStep += 1;
    setCount(countStep);
  }, [data]);

  return (
    <>
      <div className="instruction-icon-container">
        <div className="navbar-nav-link navbar-nav-link-icon rounded-pill">
          <button className="instruction-floating-icon" onClick={togglePopup}>
            {t("instruction_get_started")}
          </button>
          <span className="instruction-badge badge bg-light text-black position-absolute top-0 end-0 translate-middle-top zindex-1 rounded-pill mt-1 me-1">
            {count}
          </span>
        </div>

        {showPopup && (
          <div className="instruction-popup">
            <div className="m-b-sm">
              <h4>
                <strong>{t("instruction_getting_started")}</strong>
              </h4>
              <h5>{t("instruction.instruction_get_started_access")}</h5>
            </div>
            <div className="mt-2">
              <div className="instruction-row d-flex">
                <i className="ml-1 fa fa-circle-check text-success font-size-lg" />
                <p className="pl-2 font-weight-bolder instruction-header-color mt-1">
                  {t("instruction.complete_registration")}
                </p>
                <span className="font-size-lg ml-auto instruction-header-color">
                  &rarr;
                </span>
              </div>
              <div className="instruction-row">
                <div
                  className={`align-items-center d-flex ${
                    !isShowRequestAccessMessage ? "pl-1" : ""
                  }`}
                >
                  {isShowRequestAccessMessage && (
                    <i className="ml-1 fa fa-circle-check text-success font-size-lg" />
                  )}
                  {!isShowRequestAccessMessage && (
                    <input
                      type="radio"
                      disabled
                      className="instruction-custom-radio"
                    />
                  )}
                  <p
                    className={`pl-2 font-weight-bolder mt-1 ${
                      isShowRequestAccessMessage
                        ? "instruction-header-color"
                        : ""
                    }`}
                  >
                    {t("button.request_access")}
                  </p>
                  <span
                    className={`font-size-lg ml-auto ${
                      isShowRequestAccessMessage
                        ? "instruction-header-color"
                        : "cursor-pointer"
                    }`}
                    onClick={() => {
                      navigate("/PACSDirectory");
                    }}
                  >
                    &rarr;
                  </span>
                </div>
                <div
                  className={`p-l-sm ${
                    isShowRequestAccessMessage ? "instruction-header-color" : ""
                  }`}
                  style={{ paddingLeft: "33px" }}
                >
                  {t("instruction.request_access_locate")}{" "}
                  <label>
                    <b>{`"${t("button.request_access")}".`}</b>
                  </label>
                </div>
              </div>
              <div className="instruction-row">
                <div
                  className={`align-items-center d-flex ${
                    !isShowViewStudy ? "pl-1" : ""
                  }`}
                >
                  {isShowViewStudy && (
                    <i className="ml-1 fa fa-circle-check text-success font-size-lg" />
                  )}
                  {!isShowViewStudy && (
                    <input
                      type="radio"
                      disabled
                      className="instruction-custom-radio"
                    />
                  )}
                  <p
                    className={`pl-2 font-weight-bolder mt-1 ${
                      isShowViewStudy ? "instruction-header-color" : ""
                    }`}
                  >
                    {t("instruction.request_approved")}
                  </p>
                  <span
                    className={`font-size-lg ml-auto ${
                      isShowViewStudy
                        ? "instruction-header-color"
                        : "cursor-pointer"
                    }`}
                    onClick={() => {
                      navigate("/");
                    }}
                  >
                    &rarr;
                  </span>
                </div>
                <div
                  className={`p-l-sm ${
                    isShowViewStudy ? "instruction-header-color" : ""
                  }`}
                  style={{ paddingLeft: "33px" }}
                >
                  {t("instruction.view_studies")}
                </div>
              </div>
              {isShowViewStudy && (
                <button
                  className="btn btn-primary w-100 mt-2"
                  type="button"
                  onClick={async () => {
                    await axios.post("/users/onboarding/complete");
                    setShowPopup(!showPopup);
                    setDisableComplete(!disableComplete);
                    dispatch(InstructionAction.completedInstruction(true));
                  }}
                  disabled={disableComplete}
                >
                  {t("complete")}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};
export default InstructionStartedIcon;
